import React, { useEffect, useState } from 'react';
import axios from '../axiosInstance';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, X, Upload, Download } from 'lucide-react';

export default function Warehouses() {
  const [warehouses, setWarehouses] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tableError, setTableError] = useState('');
  const [formError, setFormError] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const [form, setForm] = useState({
    id: null,
    warehouse_name: '',
    contact_name: '',
    contact_email: '',
    contact_mobile_no: '',
    address: '',
    type: 'OPEN',
    latitude: '',
    longitude: '',
    stateId: '',
    districtId: '',
    taluka: '',
  });

  const token = localStorage.getItem('token');

  // Fetch warehouses with optional filters
  const fetchWarehouses = async (
    pageNum = 1,
    limitNum = 10,
    warehouseName = '',
    stateId = '',
    cityId = ''
  ) => {
    try {
      let url = `warehouse?page=${pageNum}&limit=${limitNum}`;
      if (warehouseName) url += `&name=${warehouseName}`;
      if (stateId) url += `&state_id=${stateId}`;
      if (cityId) url += `&district_id=${cityId}`;

      const res = await axios.get(url);

      setWarehouses(res.data.data || []);
      setPage(res.data.page || pageNum);
      setLimit(res.data.limit || limitNum);
      setTotal(res.data.total || 0);
      setTableError('');
    } catch (err) {
      console.error(
        'Error fetching warehouses:',
        err.response?.data || err.message
      );
      setTableError('⚠️ Failed to fetch warehouses. Please try again.');
    }
  };

  // Fetch states
  const fetchStates = async () => {
    try {
      const res = await axios.get('states');
      setStates(res.data || []);
    } catch (err) {
      console.error(
        'Error fetching states:',
        err.response?.data || err.message
      );
    }
  };

  // Fetch cities for a state
  const fetchCities = async (stateId) => {
    if (!stateId) return;
    try {
      const res = await axios.get(`cities/state/${stateId}`);
      setCities(res.data || []);
    } catch (err) {
      console.error(
        'Error fetching cities:',
        err.response?.data || err.message
      );
    }
  };

  // Handle form input change
  const handleChange = (e) => {
    if (e.target.name === 'stateId') {
      fetchCities(e.target.value);
      setForm((prev) => ({ ...prev, stateId: e.target.value, districtId: '' }));
    } else {
      setForm({ ...form, [e.target.name]: e.target.value });
    }
  };

  // Form validation
  const validateForm = () => {
    if (!form.warehouse_name) return 'Warehouse Name is required';
    if (!form.contact_email) return 'Contact Email is required';
    if (!form.branchId) return ' Branch is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.contact_email)) return 'Invalid Email format';

    const mobileRegex = /^[0-9]{10}$/;
    if (form.contact_mobile_no && !mobileRegex.test(form.contact_mobile_no))
      return 'Mobile number must be 10 digits';

    if (form.latitude && isNaN(Number(form.latitude)))
      return 'Latitude must be a number';
    if (form.longitude && isNaN(Number(form.longitude)))
      return 'Longitude must be a number';

    return null;
  };

  // Save or update warehouse
  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setLoading(true);
    setFormError('');

    const payload = {
      warehouse_name: form.warehouse_name,
      contact_name: form.contact_name,
      contact_email: form.contact_email,
      contact_mobile_no: form.contact_mobile_no,
      address: form.address,
      type: form.type,
      latitude: Number(form.latitude),
      longitude: Number(form.longitude),
      stateId: form.stateId,
      districtId: form.districtId,
      taluka: form.taluka,
    };

    try {
      if (form.id) {
        await axios.patch(`warehouse/${form.id}`, payload);
        alert('✅ Warehouse updated successfully!');
      } else {
        await axios.post(
          `${process.env.REACT_APP_BASE_URL}warehouse`,
          payload,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        alert('✅ Warehouse added successfully!');
      }

      setForm({
        id: null,
        warehouse_name: '',
        contact_name: '',
        contact_email: '',
        contact_mobile_no: '',
        address: '',
        type: 'OPEN',
        latitude: '',
        longitude: '',
        stateId: '',
        districtId: '',
        taluka: '',
      });
      setIsModalOpen(false);
      fetchWarehouses(page, limit, search, stateFilter, cityFilter);
    } catch (err) {
      console.error('Save error:', err.response?.data || err.message);
      setFormError(err.response?.data?.message || 'Failed to save warehouse');
    } finally {
      setLoading(false);
    }
  };

  // Edit warehouse
  const handleEdit = (wh) => {
    setForm({
      id: wh.id,
      warehouse_name: wh.warehouse_name || '',
      contact_name: wh.contact_name || '',
      contact_email: wh.contact_email || '',
      contact_mobile_no: wh.contact_mobile_no || '',
      address: wh.address || '',
      type: wh.type || 'OPEN',
      latitude: wh.latitude || '',
      longitude: wh.longitude || '',
      stateId: wh.state?.id || '',
      districtId: wh.district?.id || '',
      taluka: wh.taluka || '',
    });
    if (wh.state?.id) fetchCities(wh.state.id);
    setFormError('');
    setIsModalOpen(true);
  };

  // Add new warehouse
  const handleAdd = () => {
    setForm({
      id: null,
      warehouse_name: '',
      contact_name: '',
      contact_email: '',
      contact_mobile_no: '',
      address: '',
      type: 'OPEN',
      latitude: '',
      longitude: '',
      stateId: '',
      districtId: '',
      taluka: '',
    });
    setFormError('');
    setIsModalOpen(true);
  };

  // Download sample Excel
  const handleDownloadSample = async () => {
    try {
      const res = await axios.get('download/warehouse-upload-sample');
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'warehouse-sample.xlsx');
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download sample file');
    }
  };

  // Upload Excel
  // Upload Excel
  const handleUploadExcel = async () => {
    if (!file) return alert('Please select a file first');

    const formDataObj = new FormData();
    formDataObj.append('file', file);

    try {
      await axios.post('warehouse/excel/upload', formDataObj);
      alert('✅ Excel uploaded successfully!');
      setIsUploadModalOpen(false);
      setFile(null);
      setForm({ ...form, branchId: '' }); // reset branch selection
      fetchWarehouses();
    } catch (err) {
      console.error('Upload error:', err.response?.data || err.message);
      alert('Failed to upload Excel file');
    }
  };

  useEffect(() => {
    fetchWarehouses(page, limit, search, stateFilter, cityFilter);
    fetchStates();
  }, []);

  useEffect(() => {
    fetchWarehouses(page, limit, search, stateFilter, cityFilter);
  }, [page, limit, search, stateFilter, cityFilter]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-10  text-gray-900">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 md:mb-10 gap-4">
        <h2 className="text-3xl md:text-4xl font-extrabold text-gray-800 tracking-tight">
          🏢 Warehouses
        </h2>
        <div className="flex flex-wrap gap-3 w-full md:w-auto">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsUploadModalOpen(true)}
            className="flex items-center justify-center gap-1 md:gap-2 bg-purple-600 hover:bg-purple-700 transition text-white px-3 md:px-5 py-2 md:py-3 text-sm md:text-lg rounded-xl shadow-md font-medium flex-1 md:flex-none"
          >
            <Upload size={16} className="md:w-5 md:h-5" />{' '}
            <span className="hidden sm:inline">Upload</span> Excel
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleAdd}
            className="flex items-center justify-center gap-1 md:gap-2 bg-blue-600 hover:bg-blue-700 transition text-white px-3 md:px-6 py-2 md:py-3 text-sm md:text-lg rounded-xl shadow-md font-medium flex-1 md:flex-none"
          >
            <Plus size={16} className="md:w-5 md:h-5" />{' '}
            <span className="hidden sm:inline">Add</span> Warehouse
          </motion.button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <input
          type="text"
          placeholder="🔍 Search warehouses..."
          className="w-[300px] border border-gray-300 rounded-lg px-4 py-3 text-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="border p-3 rounded-lg"
          value={stateFilter}
          onChange={(e) => {
            const selectedState = e.target.value;
            setStateFilter(selectedState);
            setCityFilter('');
            fetchCities(selectedState);
          }}
        >
          <option value="">Filter by State</option>
          {states.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <select
          className="border p-3 rounded-lg"
          value={cityFilter}
          onChange={(e) => setCityFilter(e.target.value)}
          disabled={!stateFilter}
        >
          <option value="">Filter by City</option>
          {cities.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* Table Error */}
      {tableError && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 text-lg p-4 rounded mb-6 shadow">
          {tableError}
        </div>
      )}

      {/* Table */}
      <motion.div className="overflow-hidden rounded-2xl shadow-2xl border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-lg">
            <thead className="bg-blue-600 text-white">
              <tr>
                <th className="py-2 md:py-4 px-3 md:px-6">Warehouse</th>
                <th className="py-2 md:py-4 px-3 md:px-4">State</th>
                <th className="py-2 md:py-4 px-3 md:px-4">City</th>
                <th className="py-2 md:py-4 px-3 md:px-4">Contact</th>
                <th className="py-2 md:py-4 px-3 md:px-4">Email</th>
                <th className="py-2 md:py-4 px-3 md:px-4">Mobile</th>
                <th className="py-2 md:py-4 px-3 md:px-4">Taluka</th>
                <th className="py-2 md:py-4 px-3 md:px-4">Address</th>
                <th className="py-2 md:py-4 px-3 md:px-4 text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {warehouses.map((wh) => (
                <motion.tr
                  key={wh.id}
                  className="border-b odd:bg-gray-50 even:bg-white hover:bg-blue-50 transition text-gray-800"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <td className="py-2 md:py-4 px-3 md:px-6">
                    {wh.warehouse_name}
                  </td>
                  <td className="py-2 md:py-4 px-3 md:px-4">
                    {wh.state?.name}
                  </td>
                  <td className="py-2 md:py-4 px-3 md:px-4">
                    {wh.district?.name}
                  </td>
                  <td className="py-2 md:py-4 px-3 md:px-4">
                    {wh.contact_name}
                  </td>
                  <td className="py-2 md:py-4 px-3 md:px-4">
                    {wh.contact_email}
                  </td>
                  <td className="py-2 md:py-4 px-3 md:px-4">
                    {wh.contact_mobile_no}
                  </td>
                  <td className="py-2 md:py-4 px-3 md:px-4">{wh.taluka}</td>
                  <td className="py-2 md:py-4 px-3 md:px-4">{wh.address}</td>
                  <td className="py-2 md:py-4 px-3 md:px-4 text-center">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleEdit(wh)}
                      className="flex items-center gap-1 md:gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-2 md:px-4 py-1 md:py-2 rounded-lg shadow-sm mx-auto text-xs md:text-base"
                    >
                      <Edit2 size={14} /> Edit
                    </motion.button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Pagination */}
      <div className="flex justify-center mt-6 gap-2 md:gap-3 items-center text-sm md:text-base">
        <button
          onClick={() => page > 1 && setPage(page - 1)}
          disabled={page === 1}
          className="px-3 md:px-4 py-1 md:py-2 bg-gray-200 hover:bg-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Prev
        </button>
        <button
          onClick={() => page < totalPages && setPage(page + 1)}
          disabled={page === totalPages}
          className="px-3 md:px-4 py-1 md:py-2 bg-gray-200 hover:bg-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl p-5 md:p-10 w-[95%] md:w-[600px] max-h-[90vh] overflow-y-auto shadow-2xl relative"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
              >
                <X size={26} />
              </button>
              <h3 className="text-2xl md:text-3xl font-bold mb-5">
                {form.id ? '✏️ Edit Warehouse' : '➕ Add Warehouse'}
              </h3>
              {formError && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 text-lg p-4 rounded mb-6 shadow">
                  {formError}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <input
                  name="warehouse_name"
                  placeholder="Warehouse Name"
                  className="border p-3 rounded-lg"
                  value={form.warehouse_name}
                  onChange={handleChange}
                />
                <input
                  name="contact_name"
                  placeholder="Contact Name"
                  className="border p-3 rounded-lg"
                  value={form.contact_name}
                  onChange={handleChange}
                />
                <input
                  name="contact_email"
                  placeholder="Contact Email"
                  className="border p-3 rounded-lg"
                  value={form.contact_email}
                  onChange={handleChange}
                />
                <input
                  name="contact_mobile_no"
                  placeholder="Mobile No"
                  className="border p-3 rounded-lg"
                  value={form.contact_mobile_no}
                  onChange={handleChange}
                />
                <input
                  name="address"
                  placeholder="Address"
                  className="border p-3 rounded-lg col-span-1 md:col-span-2"
                  value={form.address}
                  onChange={handleChange}
                />
                <select
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  className="border p-3 rounded-lg"
                >
                  <option value="OPEN">OPEN</option>
                  <option value="CLOSED">CLOSED</option>
                </select>
                <input
                  type="number"
                  name="latitude"
                  placeholder="Latitude"
                  className="border p-3 rounded-lg"
                  value={form.latitude}
                  onChange={handleChange}
                />
                <input
                  type="number"
                  name="longitude"
                  placeholder="Longitude"
                  className="border p-3 rounded-lg"
                  value={form.longitude}
                  onChange={handleChange}
                />

                <select
                  name="stateId"
                  value={form.stateId}
                  onChange={handleChange}
                  className="border p-3 rounded-lg"
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <select
                  name="districtId"
                  value={form.districtId}
                  onChange={handleChange}
                  className="border p-3 rounded-lg"
                >
                  <option value="">Select City</option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <input
                  name="taluka"
                  placeholder="Taluka"
                  className="border p-3 rounded-lg"
                  value={form.taluka}
                  onChange={handleChange}
                />
              </div>
              <div className="mt-6 md:mt-8 flex justify-end space-x-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSave}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-semibold"
                >
                  {loading ? 'Saving...' : form.id ? 'Update' : 'Save'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Modal */}
      <AnimatePresence>
        {isUploadModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl p-5 md:p-8 w-full max-w-[90%] md:max-w-[500px] shadow-2xl"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
            >
              <h3 className="text-xl md:text-2xl font-bold mb-4">
                📤 Upload Excel File
              </h3>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleDownloadSample}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-xl"
              >
                <Download size={15} /> Download Sample
              </motion.button>

              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setFile(e.target.files[0])}
                className="mb-4 block w-full border p-2 rounded-lg"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setIsUploadModalOpen(false)}
                  className="bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleUploadExcel}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-semibold"
                >
                  Submit
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
